'use client';

import React from 'react';
import { FeatherCheck } from '@subframe/core';

export interface StatusTimelineStep {
  id: string;
  label: string;
}

interface StatusTimelineProps {
  title: string;
  steps: StatusTimelineStep[];
  currentIndex: number;
  badge?: React.ReactNode;
  minWidth?: string;
  className?: string;
  action?: React.ReactNode;
}

export function StatusTimeline({
  title,
  steps,
  currentIndex,
  badge,
  minWidth = 'min-w-[680px]',
  className,
  action,
}: StatusTimelineProps) {
  return (
    <div
      className={`flex flex-col gap-5 rounded-[16px] bg-neutral-50 px-6 py-5 ${className ?? ''}`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <h2 className="text-caption-bold font-caption-bold text-subtext-color tracking-wide uppercase">
            {title}
          </h2>
          {badge}
        </div>
        {action}
      </div>

      <div className="w-full overflow-x-auto">
        <div className={`flex ${minWidth} items-start`}>
          {steps.map((step, index) => {
            const isDone = index < currentIndex;
            const isCurrent = index === currentIndex;
            const leftFilled = index > 0 && index <= currentIndex;
            const rightFilled = index < currentIndex;

            return (
              <div key={step.id} className="flex flex-1 flex-col items-center">
                <div className="flex w-full items-center">
                  <div
                    className={`h-0.5 flex-1 ${index === 0 ? 'invisible' : leftFilled ? 'bg-brand-500' : 'bg-neutral-200'}`}
                  />
                  <div
                    className={`flex h-8 w-8 flex-none items-center justify-center rounded-full transition-all ${
                      isDone
                        ? 'bg-brand-600 text-white'
                        : isCurrent
                          ? 'bg-brand-600 ring-brand-100 text-white ring-4'
                          : 'text-subtext-color bg-white'
                    }`}
                  >
                    {isDone ? (
                      <FeatherCheck className="h-4 w-4" />
                    ) : (
                      <span className="text-caption-bold font-caption-bold">{index + 1}</span>
                    )}
                  </div>
                  <div
                    className={`h-0.5 flex-1 ${index === steps.length - 1 ? 'invisible' : rightFilled ? 'bg-brand-500' : 'bg-neutral-200'}`}
                  />
                </div>
                <span
                  className={`mt-2 px-1 text-center text-[11px] leading-tight ${
                    isCurrent
                      ? 'text-default-font font-semibold'
                      : isDone
                        ? 'text-default-font'
                        : 'text-subtext-color'
                  }`}
                >
                  {step.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
